import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import useData from "./useData";
import usePage from "./usePage";

const ALL_INCOMPLETE_COUNT_CACHE_TTL_MS = 30000;
const allIncompleteCountCache = new Map();

const useDashboard = () => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  // Filter state
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    startDate: null,
    endDate: null,
    clientId: null,
    projectId: null,
    supervisorId: null,
    repId: null,
  });

  // Data state for each metric
  const [opsHealth, setOpsHealth] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [callsByProject, setCallsByProject] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [callsByStatus, setCallsByStatus] = useState({
    data: null,
    isLoading: false,
    error: null,
  });
  const [callsDetail, setCallsDetail] = useState({
    data: [],
    page: 1,
    pageSize: 25,
    total: 0,
    isLoading: false,
    error: null,
  });
  const [invoicesDetail, setInvoicesDetail] = useState({
    data: [],
    page: 1,
    pageSize: 25,
    total: 0,
    isLoading: false,
    error: null,
  });

  // ===== ALL INCOMPLETE CALLS (ANY DATE) =====
  const [allIncompleteCalls, setAllIncompleteCalls] = useState({
    data: [],
    isLoading: false,
    error: null,
  });
  const [allIncompleteCount, setAllIncompleteCount] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  const buildAllIncompleteFilters = useCallback(
    (customFilters = null) => {
      const filterParams = customFilters || filters;
      const filterToSend = { ...filterParams };
      delete filterToSend.startDate;
      delete filterToSend.endDate;
      delete filterToSend.year;
      delete filterToSend.month;
      delete filterToSend.yyyymmdd;
      delete filterToSend.scheduleDate;
      return filterToSend;
    },
    [filters],
  );

  const getAllIncompleteCalls = useCallback(
    async (customFilters = null) => {
      setAllIncompleteCalls((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));
      try {
        const filterToSend = buildAllIncompleteFilters(customFilters);
        const result = await executeAction({
          entityName: "Dashboard",
          action: "allIncompleteCalls",
          data: filterToSend,
        });
        if (result?.isSuccessful && Array.isArray(result.data)) {
          setAllIncompleteCalls({
            data: result.data,
            isLoading: false,
            error: null,
          });
        } else {
          setAllIncompleteCalls({
            data: [],
            isLoading: false,
            error: result?.message || "Failed to load count",
          });
        }
      } catch (error) {
        setAllIncompleteCalls({
          data: [],
          isLoading: false,
          error: error.message || "Failed to load count",
        });
      }
    },
    [buildAllIncompleteFilters, executeAction],
  );

  const getAllIncompleteCallsCount = useCallback(
    async (customFilters = null) => {
      const filterToSend = buildAllIncompleteFilters(customFilters);
      const cacheKey = JSON.stringify(filterToSend || {});
      const cached = allIncompleteCountCache.get(cacheKey);

      if (
        cached &&
        Number.isFinite(cached.value) &&
        Date.now() - cached.at < ALL_INCOMPLETE_COUNT_CACHE_TTL_MS
      ) {
        setAllIncompleteCount({
          data: cached.value,
          isLoading: false,
          error: null,
        });
        return;
      }

      setAllIncompleteCount((prev) => ({
        ...prev,
        isLoading: true,
        error: null,
      }));
      try {
        const result = await executeAction({
          entityName: "Dashboard",
          action: "allIncompleteCallsCount",
          data: filterToSend,
        });

        if (result?.isSuccessful) {
          const countValue = Number(result?.data?.allIncompleteCount || 0);
          allIncompleteCountCache.set(cacheKey, {
            value: countValue,
            at: Date.now(),
          });

          setAllIncompleteCount({
            data: countValue,
            isLoading: false,
            error: null,
          });
        } else {
          setAllIncompleteCount((prev) => ({
            ...prev,
            isLoading: false,
            error: result?.message || "Failed to load count",
          }));
        }
      } catch (error) {
        setAllIncompleteCount((prev) => ({
          ...prev,
          isLoading: false,
          error: error.message || "Failed to load count",
        }));
      }
    },
    [buildAllIncompleteFilters, executeAction],
  );

  const exportAllIncompleteCalls = useCallback(
    async (customFilters = null) => {
      const filterToSend = buildAllIncompleteFilters(customFilters);

      const result = await executeAction({
        entityName: "Dashboard",
        action: "exportAllIncompleteCalls",
        data: filterToSend,
      });

      return result;
    },
    [buildAllIncompleteFilters, executeAction],
  );

  // Update filters with debounce to avoid excessive API calls
  const updateFilters = useCallback(
    debounce((newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      updateFilters.cancel();
    };
  }, [updateFilters]);

  // ===== OPS HEALTH =====
  const getOpsHealth = useCallback(
    async (customFilters = null) => {
      setOpsHealth((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "opsHealth",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setOpsHealth((prev) => ({
            ...prev,
            data: result.data,
            isLoading: false,
          }));
        } else {
          setOpsHealth((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch ops health",
            isLoading: false,
          }));
        }
      } catch (error) {
        setOpsHealth((prev) => ({
          ...prev,
          error: error.message || "Error fetching ops health",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== CALLS BY PROJECT =====
  const getCallsByProject = useCallback(
    async (customFilters = null) => {
      setCallsByProject((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "callsByProject",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setCallsByProject((prev) => ({
            ...prev,
            data: result.data || [],
            isLoading: false,
          }));
        } else {
          setCallsByProject((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch calls by project",
            isLoading: false,
          }));
        }
      } catch (error) {
        setCallsByProject((prev) => ({
          ...prev,
          error: error.message || "Error fetching calls by project",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== CALLS BY STATUS =====
  const getCallsByStatus = useCallback(
    async (customFilters = null) => {
      setCallsByStatus((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = customFilters || filters;
        const result = await executeAction({
          entityName: "Dashboard",
          action: "callsByStatus",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setCallsByStatus((prev) => ({
            ...prev,
            data: result.data,
            isLoading: false,
          }));
        } else {
          setCallsByStatus((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch calls by status",
            isLoading: false,
          }));
        }
      } catch (error) {
        setCallsByStatus((prev) => ({
          ...prev,
          error: error.message || "Error fetching calls by status",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== GET CALLS (detail) =====
  const getCalls = useCallback(
    async (customFilters = null, pageNumber = 1, recordsPerPage = 25) => {
      setCallsDetail((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = {
          ...(customFilters || filters),
          pageNumber,
          recordsPerPage,
        };
        const result = await executeAction({
          entityName: "Dashboard",
          action: "getCalls",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setCallsDetail((prev) => ({
            ...prev,
            data: result.data?.data || [],
            page: result.data?.page || pageNumber,
            pageSize: result.data?.pageSize || recordsPerPage,
            total: result.data?.total || 0,
            isLoading: false,
          }));
        } else {
          setCallsDetail((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch calls detail",
            isLoading: false,
          }));
        }
      } catch (error) {
        setCallsDetail((prev) => ({
          ...prev,
          error: error.message || "Error fetching calls detail",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // ===== GET INVOICES (detail) =====
  const getInvoices = useCallback(
    async (customFilters = null, pageNumber = 1, recordsPerPage = 25) => {
      setInvoicesDetail((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = {
          ...(customFilters || filters),
          pageNumber,
          recordsPerPage,
        };
        const result = await executeAction({
          entityName: "Dashboard",
          action: "getInvoices",
          data: filterParams,
        });

        if (result?.isSuccessful && result?.data) {
          setInvoicesDetail((prev) => ({
            ...prev,
            data: result.data?.data || [],
            page: result.data?.page || pageNumber,
            pageSize: result.data?.pageSize || recordsPerPage,
            total: result.data?.total || 0,
            isLoading: false,
          }));
        } else {
          setInvoicesDetail((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch invoices detail",
            isLoading: false,
          }));
        }
      } catch (error) {
        setInvoicesDetail((prev) => ({
          ...prev,
          error: error.message || "Error fetching invoices detail",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // Refresh all metrics when filters change
  useEffect(() => {
    // Debounce to avoid multiple rapid calls
    const timer = setTimeout(() => {
      getOpsHealth();
      getCallsByProject();
      getCallsByStatus();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return {
    // Filters
    filters,
    updateFilters,

    // Metrics
    opsHealth,
    callsByProject,
    callsByStatus,
    allIncompleteCount,
    allIncompleteCalls,
    // Detail data
    callsDetail,
    invoicesDetail,

    // Actions
    getOpsHealth,
    getCallsByProject,
    getCallsByStatus,
    getCalls,
    getInvoices,
    getAllIncompleteCallsCount,
    getAllIncompleteCalls,
    exportAllIncompleteCalls,

    // Utility: check if any metric is loading
    isLoading:
      opsHealth.isLoading ||
      callsByProject.isLoading ||
      callsByStatus.isLoading,

    // Utility: check if any metric has error
    hasError: opsHealth.error || callsByProject.error || callsByStatus.error,
  };
};

export default useDashboard;
